<template>
  <div :aria-busy="pending" class="relative">
    <div
      class="rounded-lg p-4 pr-3 md:flex md:justify-center"
      :class="[
        isColorful ? 'bg-brand-8 c-white' : 'bg-brand-3 c-black',
        { invisible: pending },
      ]"
      data-test-id="loyalty-banner"
    >
      <div class="flex items-center gap-4 md:gap-10">
        <div class="flex grow items-center gap-4 between">
          <vf-icon v-if="logo" class="shrink-0" size="3xl" :src="logo" />
          <div
            class="f-col grow gap-x-4"
            :class="loggedIn ? 'gap-y-2' : 'md:flex-row md:items-center'"
          >
            <p class="flex items-end gap-2">
              <span v-html="data.title" :class="loggedIn ? 'title-1' : 'title-2'" />
              <span v-if="data.extraTitle" class="overline-1">{{ data.extraTitle }}</span>
            </p>
            <p
              class="overline-1 normal-case"
              :class="{ '<md:hidden': !loggedIn }"
            >
              <span v-html="data.subtitle" />
            </p>
          </div>
        </div>
        <component
          :is="to ? BaseLink : BaseButton"
          :aria-label="to ? $t.pointsAndRewards.title : $t.simplifiedEnrollment.lookupHeading"
          class="shrink-0"
          :to
          @click="!to && handleSignIn()"
        >
          <vf-icon dir="right" name="chevron" size="lg" />
        </component>
      </div>
      <p v-if="!loggedIn" class="mt-4 overline-1 normal-case md:hidden">
        <span v-html="data.subtitle" />
      </p>
      <component
        :is="to ? BaseLink : BaseButton"
        aria-hidden="true"
        class="absolute-0"
        tabindex="-1"
        :to
        @click="!to && handleSignIn()"
      />
    </div>
    <div v-if="pending" class="absolute-0 skeleton" />
  </div>
</template>

<script lang="ts" setup>
import { BaseButton, BaseLink } from '#components'

const { $feature, $formatPrice, $t } = useNuxtApp()

const config = useAppConfig()
const { logo } = config.components.vf.loyalty
const { DialogSignUp } = useDialogsStore()
const auth = useAuthStore()
const loyalty = useLoyaltyStore()
const locale = useLocale()
const currency = useCurrencyCode()
const isMounted = useMounted()

const loggedIn = computed(() => isMounted.value && !!auth.loggedIn)
const pending = computed(() =>
  !isMounted.value || auth.pending || (loggedIn.value && (loyalty.rewardsPending || loyalty.vouchersPending))
)

const to = computed(() => loggedIn.value ? '/account/loyalty' : undefined)
const isColorful = computed(() => !loggedIn.value || loyalty.rewardsAvailable.length)

const handleSignIn = () => DialogSignUp.open({
  formLocation: 'modal:single:body:none'
})

const data = computed(() => {
  if (!loggedIn.value) {
    const subtitle1 = replaceAll($t.pointsAndRewards.banner.guest.subtitle1, {
      percent: $feature.configSignUp?.loyaltyRewardPercent ?? 10
    })
    return {
      title: $t.pointsAndRewards.banner.guest.title,
      subtitle: `${subtitle1} <br class="<md:hidden">${$t.pointsAndRewards.banner.guest.subtitle2}`,
    }
  }
  else if (loyalty.rewardsAvailable.length) {
    const reward = loyalty.rewardsAvailable
      .toSorted((a, b) =>
        new Date(a.expireDateTime || a.expirationDate).getTime()
          - new Date(b.expireDateTime || b.expirationDate).getTime()
          || (b.amount) - (a.amount)
      )[0]
    const expiresAt = reward.expireDateTime

    return {
      title: replaceAll($t.pointsAndRewards.banner.rewards.title, {
        amount: $formatPrice(reward.amount, currency, { minimumFractionDigits: 0 })
      }),
      subtitle: replaceAll($t.pointsAndRewards.banner.rewards.subtitle, { date: getFormattedDate(expiresAt, locale) })
    }
  }
  else {
    return {
      title: replaceAll($t.pointsAndRewards.banner.points.title, {
        points: getLocalizedNumber(loyalty.vouchers?.currentLoyaltyPoints || 0, locale)
      }),
      subtitle: $t.pointsAndRewards.banner.points.subtitle,
      extraTitle: replaceAll($t.pointsAndRewards.banner.points.amount, { amount: loyalty.currentReward }),
    }
  }
})
</script>
